import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosClient from '../../axios-client';
import { BlogDecisionAction, BlogLanguage, BlogReviewDecision, BlogReviewResponse, BlogReviewsResponse } from './types';

/**
 * Relecture du blog : miroir CRM de l'atelier contenu du Mac mini. Section
 * indépendante de l'établissement sélectionné, réservée aux super admins.
 *
 * Une décision part « en route » : le Mac mini la relit toutes les 2 minutes et
 * l'applique. Tant qu'une décision attend, les écrans se rafraîchissent plus vite.
 */
const CLE = 'blog-reviews';

export function useBlogReviews(enabled = true, poll = true) {
    return useQuery<BlogReviewsResponse>(
        [CLE, 'list'],
        async () => (await axiosClient.get<BlogReviewsResponse>('/super-admin/blog/reviews')).data,
        {
            enabled,
            staleTime: 30 * 1000,
            refetchInterval: poll
                ? (data) => (data?.data.some((r) => r.pending_decision) ? 20 * 1000 : 5 * 60 * 1000)
                : false,
        },
    );
}

/**
 * Pastille « Articles à relire » : paires à valider sans décision en route,
 * comptées par l'API sans télécharger la liste. Sous la clé `CLE`, donc
 * rafraîchie avec la liste après chaque décision.
 */
export function useBlogReviewsPendingCount(enabled = true, refetchInterval: number | false = 60000) {
    return useQuery<{ count: number }>(
        [CLE, 'pending-count'],
        async () => (await axiosClient.get<{ count: number }>('/super-admin/blog/reviews/pending-count')).data,
        { enabled, refetchInterval },
    );
}

export function useBlogReview(id: number) {
    return useQuery<BlogReviewResponse>(
        [CLE, 'detail', id],
        async () => (await axiosClient.get<BlogReviewResponse>(`/super-admin/blog/reviews/${id}`)).data,
        {
            enabled: Number.isFinite(id) && id > 0,
            // Décision en route : on guette son application. Retour appliqué sans nouvelle
            // version : on guette la réécriture de l'agent.
            refetchInterval: (data) => {
                const r = data?.data;
                if (!r) return false;
                if (r.pending_decision) return 20 * 1000;
                const d = r.last_applied_decision;
                return d?.action === 'retour' && d.result?.ok && d.version === r.version ? 60 * 1000 : false;
            },
        },
    );
}

/**
 * Images d'une paire (data URI, ≈ 1 Mo), chargées après le texte : la lecture
 * commence sans les attendre. Elles ne changent qu'avec la version.
 */
export function useBlogReviewImages(id: number, version: number | undefined) {
    return useQuery<Record<string, string>>(
        [CLE, 'images', id, version],
        async () => (await axiosClient.get<{ data: Record<string, string> }>(`/super-admin/blog/reviews/${id}/images`)).data.data,
        { enabled: Number.isFinite(id) && id > 0 && version !== undefined, staleTime: Infinity },
    );
}

export interface BlogDecisionPayload {
    reviewId: number;
    action: BlogDecisionAction;
    version: number;
    message?: string;
    /** Report : mardi choisi parmi `free_slots`. */
    scheduledAt?: string;
    /** Retour : langues à réviser. */
    languages?: BlogLanguage[];
    /** Échange : l'autre paire et la version lue. */
    otherReviewId?: number;
    otherVersion?: number;
}

export function useBlogDecision() {
    const queryClient = useQueryClient();

    return useMutation<BlogReviewDecision, unknown, BlogDecisionPayload>(
        async ({ reviewId, action, version, message, scheduledAt, languages, otherReviewId, otherVersion }) => {
            const { data } = await axiosClient.post<BlogReviewDecision>(`/super-admin/blog/reviews/${reviewId}/decisions`, {
                action, version, message, scheduled_at: scheduledAt, languages, other_review_id: otherReviewId, other_version: otherVersion,
            });
            return data;
        },
        { onSettled: () => queryClient.invalidateQueries(CLE) },
    );
}

/** Message d'un refus de l'API (422 version périmée, 409 décision déjà en route), sinon null. */
export function readDecisionRefusal(error: unknown): string | null {
    const response = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
    if (response?.status !== 422 && response?.status !== 409) return null;
    return response.data?.message ?? null;
}
